'use client';

import Image from 'next/image';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import LazyLiquidEther from '@/components/backgrounds/LazyLiquidEther';

/**
 * FullscreenVideoModal Component
 * -------------------------------
 * Fullscreen YouTube video overlay that covers the viewport.
 */
function FullscreenVideoModal({
  videoId,
  isOpen,
  onClose
}: {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  // Use portal to render at document body level, above everything
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      <div className="relative w-[95vw] h-[95vh] border border-white/20 rounded-2xl overflow-hidden bg-black">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:right-[90px] z-10 flex items-center gap-2 px-4 py-2 bg-black/60 hover:bg-red-500/80 rounded-lg transition-colors group"
          aria-label="Close video"
        >
          <span className="text-white/80 group-hover:text-white text-sm font-medium">Close</span>
          <svg className="w-5 h-5 text-white/80 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title="Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-0"
        />
      </div>
    </div>,
    document.body
  );
}

/**
 * VideoCard Component
 * -------------------
 * A clickable card that shows YouTube thumbnail and opens modal on click.
 */
function VideoCard({
  videoId,
  title,
  description,
  onClick
}: {
  videoId: string;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <div
      className="relative aspect-video rounded-2xl overflow-hidden bg-slate-800 cursor-pointer group"
      onClick={onClick}
    >
      {/* YouTube Thumbnail */}
      <Image
        src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
        sizes="(max-width: 768px) 100vw, 50vw"
        alt={title}
        fill
        className="object-cover group-hover:scale-105 transition-transform duration-500"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
      {/* Play Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
          <svg className="w-8 h-8 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      {/* Title */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
        <h3 className="text-white font-semibold text-lg mb-1">{title}</h3>
        <p className="text-white/60 text-sm">{description}</p>
      </div>
    </div>
  );
}

/**
 * Gallery Page
 * ============
 *
 * Showcases renders and images of Magnate Regalia.
 * Features:
 * - Categorized gallery (Exterior, Interior, Amenities)
 * - Lightbox modal for full-screen viewing
 * - Navigation between images
 * - Filter by category
 */

// Gallery images organized by category
const galleryImages = {
  exterior: [
    { id: 'cam-02', src: '/assets/images/Cam_02.webp', title: 'Building Exterior View 1', category: 'exterior' },
    { id: 'cam-04', src: '/assets/images/Cam_04.webp', title: 'Building Exterior View 2', category: 'exterior' },
    { id: 'cam-05', src: '/assets/images/Cam_05.webp', title: 'Tower Elevation', category: 'exterior' },
    { id: 'cam-06', src: '/assets/images/Cam_06.webp', title: 'Architectural View', category: 'exterior' },
    { id: 'cam-08', src: '/assets/images/Cam_08.webp', title: 'Building Facade', category: 'exterior' },
    { id: 'cam-11', src: '/assets/images/Cam_11.webp', title: 'Tower Perspective', category: 'exterior' },
    { id: 'cam-15', src: '/assets/images/Cam_15.webp', title: 'Grand View', category: 'exterior' },
    { id: 'cam-19', src: '/assets/images/Cam_19.webp', title: 'Evening Render', category: 'exterior' },
    { id: 'cam-22', src: '/assets/images/Cam_22.webp', title: 'Aerial Perspective', category: 'exterior' },
    { id: 'cam-24', src: '/assets/images/Cam_24.webp', title: 'Entrance View', category: 'exterior' },
    { id: 'cam-25', src: '/assets/images/Cam_25.webp', title: 'Landscape View', category: 'exterior' },
    { id: 'cam-27', src: '/assets/images/Cam 27.webp', title: 'Surroundings', category: 'exterior' },
    { id: 'cam-28', src: '/assets/images/Cam_28.webp', title: 'Complex Overview', category: 'exterior' },
    { id: 'cam-30', src: '/assets/images/Cam_30.webp', title: 'Full Complex View', category: 'exterior' },
  ],
  interior: [
    { id: 'foyer', src: '/assets/images/Final_Foyer_JPEG.webp', title: 'Grand Foyer', category: 'interior' },
  ],
  amenities: [
    { id: 'gym', src: '/assets/images/GYM_JPEG.webp', title: 'Gymnasium', category: 'amenities' },
    { id: 'theater', src: '/assets/images/Theater_JPEG.webp', title: 'Private Theater', category: 'amenities' },
    { id: 'cafe', src: '/assets/images/CAFE_JPEG.webp', title: 'Cafe & Lounge', category: 'amenities' },
    { id: 'banquet', src: '/assets/images/Banquet_Final_JPEG.webp', title: 'Banquet Hall', category: 'amenities' },
    { id: 'sauna', src: '/assets/images/Sauna_JPEG.webp', title: 'Sauna', category: 'amenities' },
    { id: 'steam', src: '/assets/images/steam_JPEG.webp', title: 'Steam Room', category: 'amenities' },
    { id: 'games', src: '/assets/images/INDOOR GAMES_JPEG.webp', title: 'Indoor Games', category: 'amenities' },
    { id: 'toddler', src: '/assets/images/Toddler_JPEG.webp', title: 'Kids Play Area', category: 'amenities' },
  ],
};

// Flatten all images for lightbox navigation
const allImages = [
  ...galleryImages.exterior,
  ...galleryImages.interior,
  ...galleryImages.amenities,
];

type Category = 'all' | 'exterior' | 'interior' | 'amenities';

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState<typeof allImages[0] | null>(null);
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const filteredImages = activeCategory === 'all'
    ? allImages
    : allImages.filter(img => img.category === activeCategory);

  const currentIndex = selectedImage ? filteredImages.findIndex(img => img.id === selectedImage.id) : -1;

  const navigateImage = (direction: 'prev' | 'next') => {
    if (currentIndex === -1) return;
    const newIndex = direction === 'prev'
      ? (currentIndex - 1 + filteredImages.length) % filteredImages.length
      : (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[newIndex]);
  };

  const categories: { key: Category; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: allImages.length },
    { key: 'exterior', label: 'Exterior', count: galleryImages.exterior.length },
    { key: 'interior', label: 'Interior', count: galleryImages.interior.length },
    { key: 'amenities', label: 'Amenities', count: galleryImages.amenities.length },
  ];

  return (
    <main className="min-h-screen relative overflow-hidden">
      {/* Liquid Ether Background */}
      <div className="fixed inset-0 z-0">
        <LazyLiquidEther
          colors={['#D4A574', '#8B6914', '#C9A227']}
          mouseForce={20}
          cursorSize={100}
          resolution={0.5}
          isViscous={false}
          viscous={30}
          autoDemo={true}
          autoSpeed={0.5}
          autoIntensity={2.2}
          autoResumeDelay={2000}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Content wrapper - above background */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-[108px] md:pt-32 pb-8 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-amber-400 font-medium tracking-wider mb-4">VISUAL SHOWCASE</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Gallery
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Explore our collection of stunning renders showcasing the luxury and elegance
            of Magnate Regalia. Click on any image to view in full screen.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                  activeCategory === cat.key
                    ? 'bg-amber-400 text-slate-900'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {cat.label}
                <span className={`ml-2 text-sm ${
                  activeCategory === cat.key ? 'text-slate-700' : 'text-white/50'
                }`}>
                  ({cat.count})
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Hero Image - First featured image */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden cursor-pointer group"
            onClick={() => setSelectedImage(galleryImages.exterior[6])} // Cam_15
          >
            <Image
              src="/assets/images/Cam_15.webp"
              alt="Magnate Regalia - Grand View"
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 1152px) 100vw, 1152px"
              preload
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 flex justify-between items-end">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Magnate Regalia</h2>
                <p className="text-white/70">Premium 4BHK & 5BHK Luxury Apartments</p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-white/70 text-sm">
                <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  🔍
                </span>
                Click to enlarge
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer bg-white/5"
                onClick={() => setSelectedImage(image)}
              >
                <Image
                  src={image.src}
                  alt={image.title}
                  fill
                  loading="lazy"
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-sm font-medium">{image.title}</p>
                  <p className="text-white/60 text-xs capitalize">{image.category}</p>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="min-w-12 min-h-12 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-base">
                    🔍
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Video Gallery
            </h2>
            <p className="text-white/60">
              Experience Magnate Regalia through our immersive video presentations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <VideoCard
              videoId="YrIlXflrM9E"
              title="Complete Walkthrough"
              description="Take a virtual tour of Magnate Regalia"
              onClick={() => setActiveVideo('YrIlXflrM9E')}
            />
            <VideoCard
              videoId="GJxfalJpTZc"
              title="Prime Location"
              description="Discover the strategic location advantages"
              onClick={() => setActiveVideo('GJxfalJpTZc')}
            />
          </div>
        </div>
      </section>

      {/* Brochure Request */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-amber-400/20 to-amber-600/20 rounded-3xl border border-amber-400/30 p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Request Brochure
            </h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Get detailed information about Magnate Regalia including floor plans,
              amenities, specifications, and pricing in our comprehensive brochure.
            </p>
            <a
              href="/contact?message=I%20would%20like%20to%20request%20the%20Magnate%20Regalia%20brochure.%20Please%20send%20it%20to%20my%20email%20or%20WhatsApp."
              className="inline-flex items-center gap-3 px-8 py-4 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
            >
              <span>📄</span>
              Request Brochure
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Want to See More?
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Schedule a site visit to experience Magnate Regalia in person.
            Our team will be happy to show you around.
          </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-400 text-slate-900 font-semibold rounded-lg hover:bg-amber-300 transition-colors"
            >
              Schedule a Visit
              <span>→</span>
            </a>
          </div>
        </section>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            ✕
          </button>

          {/* Navigation buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            ←
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
          >
            →
          </button>

          {/* Image container */}
          <div
            className="relative w-full h-full max-w-7xl max-h-[90vh] m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={selectedImage.src}
              alt={selectedImage.title}
              fill
              className="object-contain"
              sizes="100vw"
              loading="eager"
            />
          </div>

          {/* Image info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
            <p className="text-white font-medium mb-1">{selectedImage.title}</p>
            <p className="text-white/50 text-sm capitalize">{selectedImage.category}</p>
            <p className="text-white/60 text-xs mt-2">
              {currentIndex + 1} / {filteredImages.length}
            </p>
          </div>

          {/* Keyboard navigation hint */}
          <div className="absolute bottom-4 right-4 text-white/60 text-xs hidden md:block">
            Use ← → arrows to navigate • ESC to close
          </div>
        </div>
      )}

      {/* Video Modal - Outside main for proper z-index stacking */}
      <FullscreenVideoModal
        videoId={activeVideo || ''}
        isOpen={!!activeVideo}
        onClose={() => setActiveVideo(null)}
      />
    </main>
  );
}
